import { Button } from 'designSystem/components/Button'
import type { IntraDocumentSearchState } from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import { ChevronDown, ChevronUp, Loader2, X } from 'lucide-react'
import React, { useCallback, useEffect, useRef } from 'react'

import {
  MatchCount,
  SearchBarContainer,
  SearchInput,
  SpinningIcon,
} from './searchStyles'

type IntraDocSearchBarProps = {
  searchState: IntraDocumentSearchState
}

export function IntraDocSearchBar({ searchState }: IntraDocSearchBarProps) {
  const {
    query,
    hits,
    activeHitIndex,
    isLoading,
    setQuery,
    goToNext,
    goToPrevious,
    close,
  } = searchState

  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      } else if (
        (event.key === 'Enter' && event.shiftKey) ||
        event.key === 'ArrowUp'
      ) {
        event.preventDefault()
        goToPrevious()
      } else if (event.key === 'Enter' || event.key === 'ArrowDown') {
        event.preventDefault()
        goToNext()
      }
    },
    [close, goToNext, goToPrevious]
  )

  const matchCountText = isLoading
    ? 'Searching...'
    : hits.length === 0
      ? query.trim().length > 0
        ? 'No results'
        : ''
      : `${activeHitIndex + 1} of ${hits.length}`

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <SearchBarContainer>
      <SearchInput
        ref={inputRef}
        type='text'
        placeholder='Find in document...'
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {isLoading ? (
        <SpinningIcon>
          <Loader2 width={14} height={14} />
        </SpinningIcon>
      ) : null}
      <MatchCount>{matchCountText}</MatchCount>
      <Button
        variant='ghost'
        size='icon-sm'
        disabled={hits.length === 0}
        onClick={goToPrevious}
        title='Previous match (Shift+Enter)'>
        <ChevronUp width={14} height={14} />
      </Button>
      <Button
        variant='ghost'
        size='icon-sm'
        disabled={hits.length === 0}
        onClick={goToNext}
        title='Next match (Enter)'>
        <ChevronDown width={14} height={14} />
      </Button>
      <Button
        variant='ghost'
        size='icon-sm'
        onClick={close}
        title='Close (Escape)'>
        <X width={14} height={14} />
      </Button>
    </SearchBarContainer>
  )
}
